import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./OrderDocsModal.css";
import { getPedidoDocs } from "../../services/usuarios_cliente/obtenerDocsPedidos";
import { fileURL } from "../../utils/fileURL";

const formatMB = (bytes) => {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const normalizePath = (p) => {
  if (!p) return null;
  let s = String(p).trim().replace(/\\/g, "/").replace(/^\/+/, "");
  const idx = s.toLowerCase().indexOf("uploads/");
  if (idx >= 0) s = s.slice(idx);
  return s;
};

function OrderDocsModal({ pedidoId, onClose }) {
  const modalRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDocs = useCallback(async () => {
    if (!pedidoId) return;
    try {
      setLoading(true);
      setError("");
      const list = await getPedidoDocs(pedidoId);
      setDocs(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setError(e?.message || "No se pudieron cargar los documentos.");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [pedidoId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const onOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  useEffect(() => {
    const onKeyDown = (ev) => {
      if (ev.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => modalRef.current?.focus?.(), 0);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const getDocName = (d) => d?.original_name || d?.filename || "Documento";
  const getDocSize = (d) => d?.size_bytes || d?.size || null;

  const getDocUrl = (d) => {
    const p = d?.storage_path || d?.path || d?.ruta || d?.file || d?.archivo;
    const normalized = normalizePath(p) || p;
    return normalized ? fileURL(normalized) : null;
  };

  const getDateLabel = (d) => {
    const raw = d?.created_at || d?.fecha || d?.createdAt;
    if (!raw) return "";
    return String(raw).split("T")[0];
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const sortedDocs = useMemo(() => {
    const arr = Array.isArray(docs) ? [...docs] : [];
    arr.sort((a, b) => {
      const da = new Date(a?.created_at || a?.fecha || 0).getTime() || 0;
      const db = new Date(b?.created_at || b?.fecha || 0).getTime() || 0;
      return db - da;
    });
    return arr;
  }, [docs]);

  const hasDocs = sortedDocs.length > 0;

  return (
    <div
      className="omx-docs-scope omx-docs-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="omxDocsTitle"
      onMouseDown={onOverlayMouseDown}
    >
      <div className="omx-docs-css-root">
      <div className="omx-docs-modal" ref={modalRef} tabIndex={-1}>
        <div className="omx-docs-modal__header">
          <div>
            <div className="omx-docs-modal__title" id="omxDocsTitle">
              Documentos
            </div>
            <div className="omx-docs-modal__sub">
              Pedido <b>#{pedidoId}</b> · Facturas y archivos adjuntos a tu pedido
            </div>
          </div>

          <button
            type="button"
            className="omx-docs-modal__close"
            onClick={onClose}
            disabled={loading}
            aria-label="Cerrar"
            title="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className="omx-docs-modal__body">
          {error ? <div className="omx-docs-modal__alert">{error}</div> : null}

          <div className="omx-docs-list">
            <div className="omx-docs-list__head">
              <div className="omx-docs-list__title">Documentos subidos</div>
              {loading ? (
                <div className="omx-docs-list__loading">Cargando...</div>
              ) : null}

              <button
                type="button"
                className="omx-docs-btn omx-docs-btn--ghost"
                onClick={fetchDocs}
                disabled={loading}
                title="Actualizar"
              >
                Actualizar
              </button>
            </div>

            {!loading && !hasDocs ? (
              <div className="omx-docs-empty">
                <div className="omx-docs-empty__icon">📄</div>
                <div className="omx-docs-empty__title">Sin documentos</div>
                <div className="omx-docs-empty__sub">
                  Tus facturas y documentos aparecerán aquí cuando se adjunten al pedido.
                </div>
              </div>
            ) : null}

            {hasDocs ? (
              <div className="omx-docs-items">
                {sortedDocs.map((d, idx) => {
                  const url = getDocUrl(d);
                  const name = getDocName(d);
                  const date = getDateLabel(d);
                  const size = getDocSize(d);

                  return (
                    <div key={d?.id ?? idx} className="omx-docs-item">
                      <div className="omx-docs-item__info">
                        <div className="omx-docs-item__name" title={name}>
                          {name}
                        </div>
                        <div className="omx-docs-item__meta">
                          {date ? formatDateTime(date) : ""}
                          {size ? ` · ${formatMB(size)}` : ""}
                        </div>
                      </div>

                      <div className="omx-docs-item__actions">
                        {url ? (
                          <>
                            <a
                              className="omx-docs-btn omx-docs-btn--ghost"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Ver
                            </a>

                            <a
                              className="omx-docs-btn"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Abrir"
                            >
                              Descargar
                            </a>
                          </>
                        ) : (
                          <span className="omx-docs-item__muted">Sin enlace</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className="omx-docs-modal__footer">
          <button className="omx-docs-btn" onClick={onClose} disabled={loading}>
            Cerrar
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default OrderDocsModal;
